'use client';
import { useState, useMemo } from "react";
import { SECTIONS } from "./constants";
import { TermsSection } from "./ui/TermsSection";
import styles from './Terms.module.scss';

export function Terms() {
  const [openId, setOpenId] = useState<string | null>('general');

  const toggle = (id: string) =>
    setOpenId((prev) => (prev === id ? null : id));

  const toc = useMemo(
    () => (
      <nav className={styles.toc} aria-label="Содержание">
        <div className={styles.tocTitle}>Содержание</div>
        <ul className={styles.tocList}>
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  setOpenId(s.id);
                  document
                    .getElementById(s.id)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    ),
    [],
  );

  return (
    <div className={styles.root}>
      <header className={styles.hero}>
        <div className={styles.heroInner}>
          <span className={styles.eyebrow}>Правовая информация</span>
          <h1 className={styles.title}>Пользовательское соглашение</h1>
          <p className={styles.lead}>
            Настоящее Соглашение определяет условия использования сервиса, права и обязанности
            Сторон, а также порядок взаимодействия между Пользователем и Администрацией.
          </p>
        </div>
      </header>

      <main className={styles.content}>
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{toc}</aside>

          <div className={styles.introSections}>
            {/* 1 */}
            <TermsSection id="general" title="1. Общие положения" openId={openId} onToggle={toggle}>
              <p>
                Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения
                между <b>[Название компании]</b> (далее — «Администрация», «мы») и любым лицом,
                использующим сервис <b>Canto Projects</b>, доступный по адресу{' '}
                <b>thelightofempire.com</b> (далее — «Сервис»).
              </p>
              <p>
                Используя Сервис, включая регистрацию и авторизацию, вы подтверждаете, что
                ознакомились с условиями Соглашения в полном объёме и принимаете их без оговорок.
              </p>
              <p>
                В случае несогласия с условиями Соглашения Пользователь обязан прекратить
                использование Сервиса.
              </p>
            </TermsSection>

            {/* 2 */}
            <TermsSection id="terms" title="2. Термины и определения" openId={openId} onToggle={toggle}>
              <ul>
                <li>
                  <b>Сервис</b> — программно-аппаратный комплекс, доступный через сайт и/или
                  мобильное приложение, предоставляющий функциональные возможности, описанные
                  на Сайте.
                </li>
                <li>
                  <b>Пользователь</b> — дееспособное физическое лицо, использующее Сервис,
                  в том числе прошедшее процедуру регистрации.
                </li>
                <li>
                  <b>Аккаунт</b> — учётная запись Пользователя, позволяющая получить доступ
                  к функциональным возможностям Сервиса.
                </li>
                <li>
                  <b>Контент</b> — любые материалы (тексты, изображения, видео, аудио, ссылки
                  и иные данные), размещаемые Пользователем в Сервисе.
                </li>
                <li>
                  <b>Администрация</b> — лицо, которому принадлежат права на Сервис и которое
                  осуществляет его управление.
                </li>
              </ul>
            </TermsSection>

            {/* 3 */}
            <TermsSection id="subject" title="3. Предмет соглашения" openId={openId} onToggle={toggle}>
              <p>
                Администрация предоставляет Пользователю право на использование Сервиса
                на условиях простой (неисключительной) лицензии, без права передачи третьим
                лицам, в объёме функциональных возможностей, доступных на текущий момент.
              </p>
              <p>
                Сервис предоставляется на условиях «как есть» (as is). Администрация вправе
                изменять, дополнять или приостанавливать работу отдельных функций Сервиса
                без предварительного уведомления.
              </p>
            </TermsSection>

            {/* 4 */}
            <TermsSection id="registration" title="4. Регистрация и аккаунт" openId={openId} onToggle={toggle}>
              <p>
                Для получения доступа к отдельным функциям Сервиса Пользователь проходит
                регистрацию, указывая достоверные данные.
              </p>
              <div className={styles.cards}>
                <article className={styles.card}>
                  <div className={styles.cardBadge}>Достоверность</div>
                  <p>Пользователь несёт ответственность за актуальность и достоверность указанных при регистрации данных.</p>
                </article>
                <article className={styles.card}>
                  <div className={styles.cardBadge}>Безопасность</div>
                  <p>Пользователь обязан обеспечить конфиденциальность логина и пароля, не передавать их третьим лицам.</p>
                </article>
                <article className={styles.card}>
                  <div className={styles.cardBadge}>Возраст</div>
                  <p>Регистрация лиц младше 18 лет допускается только с согласия законных представителей.</p>
                </article>
                <article className={styles.card}>
                  <div className={styles.cardBadge}>Один аккаунт</div>
                  <p>Не допускается создание нескольких аккаунтов одним лицом в целях обхода ограничений.</p>
                </article>
              </div>
              <p className={styles.warning}>
                Все действия, совершённые с использованием аккаунта Пользователя, считаются
                совершёнными самим Пользователем.
              </p>
            </TermsSection>

            {/* 5 */}
            <TermsSection id="rights" title="5. Права и обязанности сторон" openId={openId} onToggle={toggle}>
              <h4>5.1. Пользователь вправе:</h4>
              <ul>
                <li>использовать Сервис в соответствии с его назначением и условиями Соглашения;</li>
                <li>обращаться в службу поддержки по вопросам работы Сервиса;</li>
                <li>удалить свой аккаунт в любой момент через настройки профиля.</li>
              </ul>

              <h4>5.2. Пользователь обязуется:</h4>
              <ul>
                <li>соблюдать условия настоящего Соглашения и применимое законодательство;</li>
                <li>не нарушать работу Сервиса и не предпринимать попыток несанкционированного доступа;</li>
                <li>не размещать Контент, нарушающий права третьих лиц.</li>
              </ul>

              <h4>5.3. Администрация вправе:</h4>
              <ul>
                <li>приостанавливать или прекращать доступ Пользователя к Сервису при нарушении Соглашения;</li>
                <li>удалять Контент, нарушающий условия Соглашения или законодательство;</li>
                <li>изменять функциональность Сервиса и условия Соглашения.</li>
              </ul>
            </TermsSection>

            {/* 6 */}
            <TermsSection id="content" title="6. Контент пользователя" openId={openId} onToggle={toggle}>
              <p>
                Пользователь сохраняет права на размещаемый им Контент. Размещая Контент,
                Пользователь предоставляет Администрации неисключительную, безвозмездную,
                действующую на территории всего мира лицензию на использование, хранение,
                воспроизведение и отображение такого Контента в объёме, необходимом для
                функционирования Сервиса.
              </p>
              <p>
                Пользователь гарантирует, что размещаемый им Контент не нарушает права
                интеллектуальной собственности и иные права третьих лиц.
              </p>
            </TermsSection>

            {/* 7 */}
            <TermsSection id="prohibited" title="7. Запрещённые действия" openId={openId} onToggle={toggle}>
              <p>При использовании Сервиса запрещается:</p>
              <ul>
                <li>нарушать законодательство РФ и нормы международного права;</li>
                <li>размещать материалы, содержащие вредоносный код, спам, рекламу без согласования;</li>
                <li>распространять экстремистские материалы, пропаганду насилия и ненависти;</li>
                <li>нарушать права интеллектуальной собственности третьих лиц;</li>
                <li>осуществлять массовые автоматизированные запросы (парсинг, DDoS);</li>
                <li>выдавать себя за другое лицо или представителя Администрации;</li>
                <li>использовать Сервис в целях, не предусмотренных Соглашением.</li>
              </ul>
            </TermsSection>

            {/* 8 */}
            <TermsSection id="ip" title="8. Интеллектуальная собственность" openId={openId} onToggle={toggle}>
              <p>
                Все объекты, размещённые в Сервисе и созданные Администрацией (дизайн, тексты,
                логотипы, программный код, базы данных), являются объектами исключительных прав
                Администрации или правообладателей.
              </p>
              <p>
                Использование указанных объектов без письменного разрешения правообладателя
                не допускается.
              </p>
            </TermsSection>

            {/* 9 */}
            <TermsSection id="liability" title="9. Ответственность" openId={openId} onToggle={toggle}>
              <p>
                Сервис предоставляется на условиях «как есть». Администрация не гарантирует
                бесперебойную работу Сервиса, отсутствие ошибок и сохранность данных.
              </p>
              <p>
                Администрация не несёт ответственности за:
              </p>
              <ul>
                <li>любые прямые или косвенные убытки, возникшие в результате использования или невозможности использования Сервиса;</li>
                <li>действия третьих лиц, в том числе в результате несанкционированного доступа к аккаунту;</li>
                <li>содержание Контента, размещённого Пользователями.</li>
              </ul>
              <p className={styles.warning}>
                В случае нарушения Пользователем условий Соглашения он несёт ответственность
                в соответствии с действующим законодательством.
              </p>
            </TermsSection>

            {/* 10 */}
            <TermsSection id="personal" title="10. Персональные данные" openId={openId} onToggle={toggle}>
              <p>
                Обработка персональных данных Пользователя осуществляется в соответствии
                с <b>Политикой конфиденциальности</b> и <b>Согласием на обработку персональных данных</b>,
                размещёнными на Сайте, а также Федеральным законом № 152-ФЗ «О персональных данных».
              </p>
              <p>
                Регистрируясь в Сервисе, Пользователь даёт согласие на обработку своих
                персональных данных в объёме, необходимом для предоставления функциональности Сервиса.
              </p>
            </TermsSection>

            {/* 11 */}
            <TermsSection id="termination" title="11. Срок и расторжение" openId={openId} onToggle={toggle}>
              <p>
                Соглашение вступает в силу с момента начала использования Сервиса (в том числе
                регистрации) и действует бессрочно до момента его расторжения.
              </p>
              <p>
                Пользователь вправе расторгнуть Соглашение в любой момент, удалив свой аккаунт.
                Администрация вправе расторгнуть Соглашение в одностороннем порядке при нарушении
                Пользователем его условий.
              </p>
            </TermsSection>

            {/* 12 */}
            <TermsSection id="disputes" title="12. Разрешение споров" openId={openId} onToggle={toggle}>
              <p>
                Все споры и разногласия, возникающие из настоящего Соглашения, разрешаются путём
                переговоров. Стороны устанавливают обязательный досудебный порядок: направление
                письменной претензии и её рассмотрение в течение 30 (тридцати) календарных дней.
              </p>
              <p>
                При невозможности урегулирования спора путём переговоров он передаётся
                на рассмотрение в суд по месту нахождения Администрации в соответствии
                с законодательством РФ.
              </p>
            </TermsSection>

            {/* 13 */}
            <TermsSection id="changes" title="13. Изменения соглашения" openId={openId} onToggle={toggle}>
              <p>
                Администрация вправе в одностороннем порядке вносить изменения в настоящее
                Соглашение. Новая редакция вступает в силу с момента её публикации на Сайте,
                если иное не указано в самой редакции.
              </p>
              <p>
                Продолжение использования Сервиса после публикации изменений означает согласие
                Пользователя с новой редакцией Соглашения.
              </p>
            </TermsSection>

            {/* 14 */}
            <TermsSection id="contacts" title="14. Контакты" openId={openId} onToggle={toggle}>
              <ul className={styles.contacts}>
                <li><span>VK:</span> <a href="https://vk.ru/thelightofempire">https://vk.ru/thelightofempire</a></li>
                {/* <li><span>Телефон:</span> <a href="tel:+70000000000">+7 (000) 000-00-00</a></li>
                <li><span>Адрес:</span> г. Москва, ул. Примерная, д. 1</li> */}
              </ul>
            </TermsSection>

            <div className={styles.consent}>
              <b>Принятие условий.</b> Регистрируясь в Сервисе или продолжая его использование,
              вы подтверждаете, что ознакомлены с настоящим Пользовательским соглашением
              и принимаете его условия в полном объёме без оговорок и исключений.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
